import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { CheckCircle2, PhoneCall, Plus, Upload, UserPlus } from 'lucide-react'
import { useState } from 'react'

import { ApiError, errorMessage } from '@/api/errors'
import {
  useCurrentPermissions,
  useLead,
  useLeadMutations,
  useLeads,
  useMembers,
} from '@/api/queries'
import type { Lead, LeadSource, LeadStatus, Uuid } from '@/api/types'
import { LEAD_SOURCES } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { MessagesSection } from '@/features/crm/messages-section'
import { TasksSection } from '@/features/crm/tasks-section'
import { notify } from '@/lib/toast'

/**
 * I contatti da lavorare — F6, CRM-01.
 *
 * ## L'elenco è una lista di cose da fare, non un archivio
 *
 * Ordina per **prossima azione** e per default lascia fuori le trattative chiuse: lo
 * fa il server, e ripetere l'ordinamento qui sarebbe un secondo criterio che si
 * scopre diverso dal primo. Chi non ha una prossima azione sta in fondo con la
 * frase che lo dice — un contatto senza prossima azione è un contatto che nessuno
 * chiamerà, e questa pagina esiste per farlo notare.
 *
 * ## Convertire non è «segnare come vinto»
 *
 * `won` non si scrive: si converte, e nasce l'anagrafica. Il pulsante porta quindi
 * alla scheda dell'iscritto appena creato, perché il gesto dopo è quasi sempre
 * vendergli un abbonamento. Se il contatto era già stato convertito, il server
 * risponde `409` **con l'id**: la pagina ci porta invece di mostrare un errore, che
 * è ciò che serve a chi ha premuto due volte.
 *
 * ## Perso pretende un motivo
 *
 * Il dialogo lo chiede nello stesso momento in cui si cambia stato. Chiederlo dopo
 * significa non averlo mai, e «perché non si è iscritto» è l'unico dato che rende
 * utile una trattativa persa.
 */
export const Route = createFileRoute('/_app/contatti/')({
  component: LeadsPage,
})

const SOURCE_LABELS: Record<LeadSource, string> = {
  walk_in: 'Passato in palestra',
  web_form: 'Modulo web',
  referral: 'Presentato da un iscritto',
  social: 'Social',
  campaign: 'Campagna',
  phone: 'Telefono',
  other: 'Altro',
}

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'Nuovo',
  contacted: 'Contattato',
  trial_booked: 'Prova prenotata',
  negotiating: 'In trattativa',
  won: 'Iscritto',
  lost: 'Perso',
}

/** Gli stati che si possono scrivere: `won` lo mette la conversione. */
const WRITABLE_STATUSES = ['new', 'contacted', 'trial_booked', 'negotiating', 'lost'] as const

const today = () => new Date().toISOString().slice(0, 10)

function LeadsPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('lead:read')
  const canWrite = permissions.has('lead:write')
  const canConvert = canWrite && permissions.has('gym_member:write')

  const [closed, setClosed] = useState(false)
  const [query, setQuery] = useState('')
  const list = useLeads({
    closed,
    ...(query.trim().length >= 2 ? { q: query.trim() } : {}),
  })
  const [creating, setCreating] = useState(false)
  const [opened, setOpened] = useState<Uuid | null>(null)

  if (!canRead) {
    return (
      <>
        <PageHeader title="Contatti" description="Chi si è fatto vivo e non è ancora iscritto." />
        <Alert variant="neutral" title="Serve «lead:read»" icon={false}>
          Il tuo ruolo non legge i contatti. È il permesso del banco e di chi guida la palestra: una
          trattativa non è un allenamento.
        </Alert>
      </>
    )
  }

  const rows = list.data?.data ?? []

  if (list.isError && !list.data) {
    return <ErrorState message={errorMessage(list.error)} onRetry={() => void list.refetch()} />
  }

  return (
    <>
      <PageHeader
        title="Contatti"
        description="Chi va richiamato oggi sta in cima. Un contatto senza prossima azione è un contatto che nessuno chiamerà."
        actions={
          canWrite ? (
            <div className="flex gap-2">
              {/* L'import sta accanto a «Nuovo contatto» e non in un menù: una lista di
                  contatti si carica il primo giorno, e il primo giorno questa pagina è
                  vuota — un pulsante nascosto in un menù non lo si trova. */}
              <Button size="sm" variant="outline" asChild>
                <Link to="/contatti/import">
                  <Upload className="size-4" />
                  Importa una lista
                </Link>
              </Button>
              <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
                <Plus className="size-4" />
                Nuovo contatto
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Field label="Cerca" className="w-56" hint="Nome, cognome, email o telefono.">
          {(props) => (
            <Input
              {...props}
              value={query}
              placeholder="Rossi"
              onChange={(event) => setQuery(event.target.value)}
            />
          )}
        </Field>
        <div className="flex gap-2 pb-0.5">
          <Button size="sm" variant={closed ? 'ghost' : 'outline'} onClick={() => setClosed(false)}>
            Da lavorare
          </Button>
          <Button size="sm" variant={closed ? 'outline' : 'ghost'} onClick={() => setClosed(true)}>
            Chiuse
          </Button>
        </div>
      </div>

      {list.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<PhoneCall />}
          title={closed ? 'Nessuna trattativa chiusa' : 'Nessun contatto da lavorare'}
          description="Un contatto è una persona che si è fatta viva e non è ancora iscritta. Serve almeno un recapito: senza, è un appunto."
        />
      ) : (
        <ul className="divide-border divide-y">
          {rows.map((lead) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              canWrite={canWrite}
              canConvert={canConvert}
              onOpen={() => setOpened(lead.id)}
            />
          ))}
        </ul>
      )}

      {creating ? <LeadDialog leadId={null} onClose={() => setCreating(false)} /> : null}
      {opened !== null ? <LeadDialog leadId={opened} onClose={() => setOpened(null)} /> : null}
    </>
  )
}

function LeadRow({
  lead,
  canWrite,
  canConvert,
  onOpen,
}: {
  lead: Lead
  canWrite: boolean
  canConvert: boolean
  onOpen: () => void
}) {
  const mutations = useLeadMutations()
  const navigate = useNavigate()

  /**
   * Converte e porta alla scheda dell'iscritto: il gesto dopo è quasi sempre
   * vendergli un abbonamento, e lasciare la persona in questo elenco costringerebbe
   * a cercarla in un altro.
   *
   * Un `409 lead_already_converted` porta l'id dell'anagrafica: ci si va comunque,
   * perché chi ha premuto due volte vuole quella scheda e non un errore.
   */
  async function convert(): Promise<void> {
    try {
      const member = await mutations.convert.mutateAsync({ id: lead.id })
      notify({ message: 'Contatto convertito in iscritto.', variant: 'success' })
      await navigate({ to: '/iscritti/$gymMemberId', params: { gymMemberId: member.id } })
    } catch (cause) {
      const already =
        cause instanceof ApiError && cause.details?.['reason'] === 'lead_already_converted'
          ? (cause.details['gymMemberId'] as string | undefined)
          : undefined
      if (already !== undefined) {
        notify({ message: 'Era già stato convertito: ecco la scheda.' })
        await navigate({ to: '/iscritti/$gymMemberId', params: { gymMemberId: already } })
        return
      }
      notify({ message: errorMessage(cause), variant: 'danger' })
    }
  }

  const due =
    lead.nextActionAt === null
      ? 'nessuna prossima azione'
      : lead.nextActionAt <= today()
        ? `da fare (${lead.nextActionAt})`
        : `il ${lead.nextActionAt}`

  return (
    <li className="flex flex-wrap items-center gap-3 py-3 first:pt-0">
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {lead.lastName} {lead.firstName}
        </p>
        <p className="text-muted-foreground text-xs">
          {SOURCE_LABELS[lead.source]} · {due}
          {lead.assignedName === null ? ' · di nessuno' : ` · ${lead.assignedName}`}
          {lead.phone === null ? '' : ` · ${lead.phone}`}
          {lead.lostReason === null ? '' : ` · «${lead.lostReason}»`}
        </p>
      </div>
      <StatusBadge status={lead.status} />
      <div className="flex gap-1.5">
        <Button size="sm" variant="ghost" onClick={onOpen}>
          {canWrite ? 'Modifica' : 'Apri'}
        </Button>
        {canConvert && lead.gymMemberId === null ? (
          <Button
            size="sm"
            variant="outline"
            loading={mutations.convert.isPending}
            onClick={() => void convert()}
          >
            <UserPlus className="size-4" />
            Iscrivi
          </Button>
        ) : null}
        {lead.gymMemberId !== null ? (
          <Button size="sm" variant="ghost" asChild>
            <a href={`/iscritti/${lead.gymMemberId}`}>
              <CheckCircle2 className="size-4" />
              Scheda
            </a>
          </Button>
        ) : null}
      </div>
    </li>
  )
}

function StatusBadge({ status }: { status: LeadStatus }) {
  const tone =
    status === 'won'
      ? 'success'
      : status === 'lost'
        ? 'danger'
        : status === 'new'
          ? 'primary'
          : 'neutral'
  return <Badge variant={tone}>{STATUS_LABELS[status]}</Badge>
}

/**
 * Creazione e modifica.
 *
 * `source` compare **solo** alla creazione: da dove è arrivata una persona è un
 * fatto del passato, e correggerlo a trattativa avviata significa quasi sempre
 * riscrivere una statistica invece di correggere un dato — il server infatti non lo
 * accetta.
 */
function LeadDialog({ leadId, onClose }: { leadId: Uuid | null; onClose: () => void }) {
  const existing = useLead(leadId)
  const mutations = useLeadMutations()
  const members = useMembers({ status: 'active' })

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [source, setSource] = useState<LeadSource>('phone')
  const [status, setStatus] = useState<(typeof WRITABLE_STATUSES)[number]>('new')
  const [assignedMemberId, setAssignedMemberId] = useState<Uuid | undefined>(undefined)
  const [nextActionAt, setNextActionAt] = useState('')
  const [nextActionNote, setNextActionNote] = useState('')
  const [lostReason, setLostReason] = useState('')
  const [loaded, setLoaded] = useState(leadId === null)
  const [error, setError] = useState<string | null>(null)

  if (!loaded && existing.data !== undefined) {
    const lead = existing.data.data
    setFirstName(lead.firstName)
    setLastName(lead.lastName)
    setEmail(lead.email ?? '')
    setPhone(lead.phone ?? '')
    setSource(lead.source)
    if (lead.status !== 'won') setStatus(lead.status)
    setAssignedMemberId(lead.assignedMemberId ?? undefined)
    setNextActionAt(lead.nextActionAt ?? '')
    setNextActionNote(lead.nextActionNote ?? '')
    setLostReason(lead.lostReason ?? '')
    setLoaded(true)
  }

  const pending = mutations.create.isPending || mutations.update.isPending

  async function submit(): Promise<void> {
    if (firstName.trim() === '' || lastName.trim() === '') {
      setError('Nome e cognome sono obbligatori.')
      return
    }
    if (email.trim() === '' && phone.trim() === '') {
      setError('Serve almeno un recapito: un contatto che non si può richiamare è un appunto.')
      return
    }
    if (status === 'lost' && lostReason.trim() === '') {
      setError('Indica perché la trattativa è stata persa.')
      return
    }
    setError(null)

    const shared = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim() === '' ? null : email.trim(),
      phone: phone.trim() === '' ? null : phone.trim(),
      assignedMemberId: assignedMemberId ?? null,
      nextActionAt: nextActionAt === '' ? null : nextActionAt,
      nextActionNote: nextActionNote.trim() === '' ? null : nextActionNote.trim(),
    }

    try {
      if (leadId === null) {
        await mutations.create.mutateAsync({ ...shared, source })
        notify({ message: 'Contatto registrato.', variant: 'success' })
      } else {
        await mutations.update.mutateAsync({
          id: leadId,
          etag: existing.data?.etag ?? null,
          patch: {
            ...shared,
            status,
            lostReason: lostReason.trim() === '' ? null : lostReason.trim(),
          },
        })
        notify({ message: 'Contatto aggiornato.', variant: 'success' })
      }
      onClose()
    } catch (cause) {
      setError(errorMessage(cause))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{leadId === null ? 'Nuovo contatto' : 'Contatto'}</DialogTitle>
          <DialogDescription>
            «Vinto» non si scrive: si ottiene iscrivendo la persona, e da lì nasce la sua
            anagrafica.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {error !== null ? (
            <Alert variant="danger" title="Non salvato">
              {error}
            </Alert>
          ) : null}

          {!loaded ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Nome" required>
                {(props) => (
                  <Input
                    {...props}
                    value={firstName}
                    maxLength={120}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Cognome" required>
                {(props) => (
                  <Input
                    {...props}
                    value={lastName}
                    maxLength={120}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Telefono" hint="Email oppure telefono: almeno uno.">
                {(props) => (
                  <Input
                    {...props}
                    value={phone}
                    maxLength={32}
                    onChange={(event) => setPhone(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Email">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>
              {leadId === null ? (
                <Field
                  label="Da dove arriva"
                  required
                  hint="Non si cambia dopo: è un fatto del passato."
                >
                  {(props) => (
                    <Select
                      {...props}
                      value={source}
                      onValueChange={(value) => setSource(value as typeof source)}
                      aria-label="Sorgente"
                      items={LEAD_SOURCES.map((value) => ({
                        value,
                        label: SOURCE_LABELS[value],
                      }))}
                    />
                  )}
                </Field>
              ) : (
                <Field label="Stato">
                  {(props) => (
                    <Select
                      {...props}
                      value={status}
                      onValueChange={(value) => setStatus(value as typeof status)}
                      aria-label="Stato"
                      items={WRITABLE_STATUSES.map((value) => ({
                        value,
                        label: STATUS_LABELS[value],
                      }))}
                    />
                  )}
                </Field>
              )}
              <Field label="Chi lo segue" hint="Vuoto: di nessuno, e si vede nell'elenco.">
                {(props) => (
                  <Select
                    {...props}
                    value={assignedMemberId}
                    onValueChange={setAssignedMemberId}
                    aria-label="Chi lo segue"
                    placeholder="Nessuno"
                    items={(members.data?.data ?? []).map((member) => ({
                      value: member.memberId,
                      label: (
                        <span className="inline-flex items-center gap-2">
                          {member.name ?? member.email}
                          <span className="text-muted-foreground text-xs">{member.role}</span>
                        </span>
                      ),
                    }))}
                  />
                )}
              </Field>
              <Field label="Prossima azione" hint="Quando richiamarlo.">
                {(props) => (
                  <Input
                    {...props}
                    type="date"
                    value={nextActionAt}
                    onChange={(event) => setNextActionAt(event.target.value)}
                  />
                )}
              </Field>
              <Field label="Cosa fare" className="sm:col-span-2">
                {(props) => (
                  <Input
                    {...props}
                    value={nextActionNote}
                    maxLength={500}
                    placeholder="Richiamare dopo le 18"
                    onChange={(event) => setNextActionNote(event.target.value)}
                  />
                )}
              </Field>
              {leadId !== null && status === 'lost' ? (
                <Field
                  label="Perché è persa"
                  required
                  className="sm:col-span-2"
                  hint="È l'unico dato che rende utile una trattativa persa."
                >
                  {(props) => (
                    <Input
                      {...props}
                      value={lostReason}
                      maxLength={200}
                      placeholder="Troppo caro"
                      onChange={(event) => setLostReason(event.target.value)}
                    />
                  )}
                </Field>
              ) : null}
            </div>
          )}
          {/*
            CRM-08 dentro il contatto, e non in una pagina a parte: si annota mentre
            si e' al telefono con quella persona, e un secondo posto in cui scrivere
            significherebbe non scrivere.
          */}
          {leadId !== null ? (
            <div className="border-border border-t pt-3">
              <p className="typo-label-lg mb-2 font-semibold">Note e attività</p>
              <TasksSection leadId={leadId} />
            </div>
          ) : null}
          {/*
            CRM-03, per la stessa ragione delle note: «perché non gli è arrivato niente» si
            chiede al telefono con quella persona. Un lead riceve promozioni prima ancora di
            essere iscritto, quindi la disiscrizione serve qui quanto sulla scheda di un
            iscritto — e chi la chiede, la chiede a chi sta chiamando.
          */}
          {leadId !== null ? (
            <div className="border-border border-t pt-3">
              <p className="typo-label-lg mb-2 font-semibold">Comunicazioni</p>
              <MessagesSection leadId={leadId} />
            </div>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button
            variant="primary"
            loading={pending}
            disabled={!loaded}
            onClick={() => void submit()}
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
